import React, { useState } from "react";
import { useNavigate, Navigate, Link, useLocation } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import Logo from "@/components/Logo";
import { getStaffLandingPath } from "@/lib/page-permissions";

export default function Login() {
  const { user, loginStaff } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const resetSuccess = location.state?.resetSuccess;
  const setupSuccess = location.state?.setupSuccess;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user.user_type === "staff") return <Navigate to={getStaffLandingPath(user)} replace />;
  if (user && user.user_type === "client") return <Navigate to="/portal" replace />;

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const staff = await loginStaff(email, password);
      navigate(getStaffLandingPath(staff), { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6" style={{ background: "var(--fb-bg)" }}>
      <div className="grid md:grid-cols-2 max-w-5xl w-full rounded-2xl overflow-hidden fb-card">
        {/* Left image panel */}
        <div className="hidden md:block relative" style={{ minHeight: 540 }}>
          <img
            src="https://images.pexels.com/photos/10802295/pexels-photo-10802295.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Palouse Hills"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(26,28,26,0.2) 0%, rgba(5,47,102,0.88) 100%)" }} />
          <div className="relative z-10 p-10 h-full flex flex-col justify-end text-white">
            <div className="flex items-center gap-3 mb-6">
              <Logo size={56} className="bg-white/15 backdrop-blur" />
              <div>
                <div className="font-bold text-xl" style={{ fontFamily: "Outfit" }}>Pullman Child Welfare</div>
                <div className="text-sm opacity-80">Client Management System</div>
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-3" style={{ fontFamily: "Outfit" }}>
              Serving our<br/>community.
            </h1>
            <p className="text-base opacity-90 max-w-sm">
              A purpose-built tool for fast Saturday morning check-ins, family records, and seasonal programs.
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="p-6 sm:p-10 md:p-14 bg-white">
          <h2 className="text-2xl sm:text-3xl font-bold mb-2" style={{ fontFamily: "Outfit" }}>Staff sign in</h2>
          <p className="text-sm mb-8" style={{ color: "var(--fb-text-muted)" }}>
            Access the client management dashboard.
          </p>

          {resetSuccess && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-lg fb-status-success text-sm mb-6" data-testid="reset-success-banner">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              Password updated — you can now sign in with your new password.
            </div>
          )}
          {setupSuccess && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-lg fb-status-success text-sm mb-6" data-testid="setup-success-banner">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              Account activated — you can now sign in with your new password.
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-lg border-2 outline-none focus:border-[#052F66] transition-all"
                style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium">Password</label>
                <Link to="/forgot-password" className="text-xs font-medium underline"
                  style={{ color: "var(--fb-text-muted)" }} data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-lg border-2 outline-none focus:border-[#052F66] transition-all"
                style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-lg fb-status-error text-sm" data-testid="login-error">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full fb-btn-primary py-3.5 rounded-lg font-semibold text-base shadow-sm"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t text-sm" style={{ borderColor: "var(--fb-border)" }}>
            <p style={{ color: "var(--fb-text-muted)" }}>
              Are you a client?{" "}
              <a
                href="/portal/login"
                data-testid="client-portal-link"
                className="font-medium underline"
                style={{ color: "var(--fb-primary)" }}
              >
                Use the client portal →
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
